import { useTranslation } from "react-i18next";
import Modal, { ModalHead } from "@/shared/ui/components/modal";
import Button from "@/shared/ui/components/button";
import ScheduleList from "@/shared/ui/components/schedule-list";
import Badge from "@/shared/ui/components/badge";
import Box from "@/shared/ui/components/box";
import MoneyAmount from "@/shared/ui/components/money-amount";
import type { AppContext, PaymentOption } from "@/types";
import * as events from "@/events";
import {
  isUaeBnplPlusProduct,
  termCostLineItemLabelKey,
} from "@/lib/payment-option";
import {
  getSubventionFeeAfterWaiverAmount,
  getSubventionFeeStrikethroughAmount,
  shouldShowSubventionFeeStrikethrough,
} from "@/lib/subvention";
import { getPaymentsLabelKey } from "../lib/payments-label";

interface PlanDetailsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  ctx: AppContext;
  option: PaymentOption;
}

/**
 * Read-only schedule details for the single-page checkout pre-selected plan.
 * Reuses the same bottom-sheet primitive (Modal) and schedule rendering
 * (ScheduleList) as OrderDetailsModal, but exposes a single "Got it" action
 * with no plan/tenure switching.
 *
 * Order-summary fee rows (CPF / term cost / total fees) mirror OrderDetailsModal
 * so SPC "see details" stays fee-parity with multi-step plan selection.
 */
export function PlanDetailsSheet({
  isOpen,
  onClose,
  ctx,
  option,
}: PlanDetailsSheetProps) {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  const { schedulePreview } = option;
  const processingFeeAmount = option.processingFeeAmount?.amount ?? 0;
  const murabahaProfit = option.termCostAmount?.amount ?? 0;
  const totalFees = option.totalCustomerFeeAmount?.amount ?? 0;
  const feeCurrency =
    option.processingFeeAmount?.currency ??
    option.totalCustomerFeeAmount?.currency ??
    option.totalToBeRepaidAmount.currency;
  const isUaeBnplPlus = isUaeBnplPlusProduct(
    ctx.session.order.countryCode,
    option.productType,
  );
  const showSubventionFeeStrikethrough =
    shouldShowSubventionFeeStrikethrough(option);
  const strikethroughFeeAmount =
    getSubventionFeeStrikethroughAmount(option);
  const feeAfterWaiverAmount = getSubventionFeeAfterWaiverAmount(option);

  return (
    <Modal
      onClose={onClose}
      stickyFooter={
        <Button
          size="large"
          onClick={onClose}
          data-testid="plan-details-got-it"
        >
          {t("single_page_checkout.got_it")}
        </Button>
      }
    >
      <div className="pt-6 pr-6 pl-6 border-gray-200 shrink-0">
        <ModalHead onClose={onClose} titleAlignment="left">
          {t("plan.details_title")}
        </ModalHead>
      </div>

      <div className="px-6 space-y-6 overflow-y-auto flex-1 min-h-0 pt-4">
        <div className="space-y-4 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-[#16181D] font-medium">
              {t("order.payment_plan")}
            </span>
            <Badge>
              {option.tenure}{" "}
              {t(getPaymentsLabelKey(i18n.language, option.tenure), {
                count: option.tenure,
              })}
            </Badge>
          </div>

          <ScheduleList
            items={schedulePreview.map(({ dueDate, amount, dueNow }) => [
              dueNow ? ctx.t("order.due_today") : ctx.displayDate(dueDate),
              <MoneyAmount
                key={`${dueDate}-${amount.amount}`}
                amount={amount.amount}
                currency={amount.currency}
                language={i18n.language}
              />,
            ])}
            hideRange={
              schedulePreview.length <= 3
                ? undefined
                : [3, schedulePreview.length - 1]
            }
            expandLabel={t("order.view_all_payments")}
            collapseLabel={t("order.collapse_payments")}
            onExpandClick={() =>
              ctx.sendEvent(events.SCHEDULE_LIST_EXPAND_CLICKED)
            }
            onCollapseClick={() =>
              ctx.sendEvent(events.SCHEDULE_LIST_COLLAPSE_CLICKED)
            }
          />
        </div>

        <Box radius="sm" className="p-4">
          <div className="space-y-3">
            <div
              className="flex justify-between text-sm"
              data-testid="plan-details-purchase-amount"
            >
              <span className="text-[#585B7A]">
                {t("order.purchase_amount")}
              </span>
              <MoneyAmount
                amount={ctx.session.orderTotalAmount.amount}
                currency={ctx.session.orderTotalAmount.currency}
                language={i18n.language}
              />
            </div>

            {murabahaProfit > 0 || processingFeeAmount > 0 ? (
              <>
                {murabahaProfit > 0 && (
                  <div
                    className="flex justify-between items-center text-sm gap-2 min-w-0"
                    data-testid="plan-details-term-cost"
                  >
                    <span className="text-[#585B7A] shrink-0">
                      {t(termCostLineItemLabelKey(isUaeBnplPlus))}
                    </span>
                    <span className="shrink-0">
                      <MoneyAmount
                        amount={murabahaProfit}
                        currency={feeCurrency}
                        language={i18n.language}
                      />
                    </span>
                  </div>
                )}
                {processingFeeAmount > 0 && (
                  <div
                    className="flex justify-between items-center text-sm gap-2 min-w-0"
                    data-testid="plan-details-processing-fee"
                  >
                    <span className="text-[#585B7A] shrink-0">
                      {t("total.processing_fee")}
                    </span>
                    <span className="shrink-0">
                      <MoneyAmount
                        amount={processingFeeAmount}
                        currency={feeCurrency}
                        language={i18n.language}
                      />
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div
                className="flex justify-between items-center text-sm gap-2 min-w-0"
                data-testid="plan-details-total-fees"
              >
                <span className="text-[#585B7A] shrink-0">
                  {t("order.total_fees")}
                </span>
                {showSubventionFeeStrikethrough ? (
                  <div
                    className="flex items-center gap-2 shrink-0 flex-nowrap"
                    data-testid="plan-details-total-fees-strikethrough"
                  >
                    <span className="line-through text-[rgba(126,130,173,1)]">
                      <MoneyAmount
                        amount={strikethroughFeeAmount}
                        currency={feeCurrency}
                        className="line-through"
                        language={i18n.language}
                      />
                    </span>
                    <span className="text-[#128351] font-medium">
                      <MoneyAmount
                        amount={feeAfterWaiverAmount}
                        currency={feeCurrency}
                        language={i18n.language}
                      />
                    </span>
                  </div>
                ) : totalFees === 0 ? (
                  <span className="text-[#128351] font-medium shrink-0">
                    <MoneyAmount
                      amount={0}
                      currency={feeCurrency}
                      language={i18n.language}
                    />
                  </span>
                ) : (
                  <span className="shrink-0">
                    <MoneyAmount
                      amount={totalFees}
                      currency={feeCurrency}
                      language={i18n.language}
                    />
                  </span>
                )}
              </div>
            )}

            <div className="border-t border-[#DAE0EB]" />

            <div
              className="flex justify-between items-center text-base font-medium"
              data-testid="plan-details-total-of-payments"
            >
              <span>{t("order.total_of_payments")}</span>
              <MoneyAmount
                amount={option.totalToBeRepaidAmount.amount}
                currency={option.totalToBeRepaidAmount.currency}
                className="font-medium"
                language={i18n.language}
              />
            </div>
          </div>
        </Box>
      </div>
    </Modal>
  );
}
